import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, BarChart3, X, EyeOff, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";

function NewPollDialog({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ question: "", description: "", options: [{ id: crypto.randomUUID(), text: "" }, { id: crypto.randomUUID(), text: "" }], multiple: false, anonymous: false, closes_at: "" });
  const setOpt = (i, v) => setF({ ...f, options: f.options.map((o, j) => (j === i ? { ...o, text: v } : o)) });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/polls", { ...f, options: f.options.map((o) => o.text), closes_at: f.closes_at ? new Date(f.closes_at).toISOString() : null });
      toast.success("Sondaggio pubblicato");
      onOpenChange(false);
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="poll-dialog">
        <DialogHeader><DialogTitle>Nuovo sondaggio</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Domanda</Label><Input value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} required data-testid="poll-question-input" /></div>
          <div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="poll-description-input" /></div>
          <div className="space-y-2">
            <Label>Opzioni</Label>
            {f.options.map((o, i) => (
              <div key={o.id} className="flex gap-2">
                <Input value={o.text} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Opzione ${i + 1}`} required data-testid={`poll-option-input-${i}`} />
                {f.options.length > 2 && <Button type="button" variant="ghost" size="icon" onClick={() => setF({ ...f, options: f.options.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, options: [...f.options, { id: crypto.randomUUID(), text: "" }] })} data-testid="poll-add-option-button"><Plus className="mr-1 h-3.5 w-3.5" />Aggiungi opzione</Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center justify-between rounded-md border px-3 py-2.5 text-sm">Scelta multipla<Switch checked={f.multiple} onCheckedChange={(c) => setF({ ...f, multiple: c })} data-testid="poll-multiple-switch" /></label>
            <label className="flex items-center justify-between rounded-md border px-3 py-2.5 text-sm">Anonimo<Switch checked={f.anonymous} onCheckedChange={(c) => setF({ ...f, anonymous: c })} data-testid="poll-anonymous-switch" /></label>
          </div>
          <div className="space-y-1.5"><Label>Chiusura (opzionale)</Label><Input type="datetime-local" value={f.closes_at} onChange={(e) => setF({ ...f, closes_at: e.target.value })} data-testid="poll-closes-input" /></div>
          <DialogFooter><Button type="submit" data-testid="poll-submit-button">Pubblica</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Sondaggi() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/polls").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="polls-page">
      <PageHeader title="Sondaggi" subtitle="Raccogli e condividi l'opinione del team."
        actions={isAdmin && <Button onClick={() => setOpen(true)} data-testid="new-poll-button"><Plus className="mr-1.5 h-4 w-4" />Nuovo sondaggio</Button>} />
      {items.length === 0 ? (
        <Empty icon={BarChart3} text="Nessun sondaggio." testId="polls-empty" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((p, i) => (
            <Link key={p.id} to={`/sondaggi/${p.id}`} className="lift fade-up rounded-lg border bg-card p-5" style={{ animationDelay: `${i * 40}ms` }} data-testid={`poll-card-${p.id}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className={p.is_open ? "font-semibold text-primary" : "text-muted-foreground"}>{p.is_open ? "Aperto" : "Chiuso"}</span>
                {p.anonymous && <span className="inline-flex items-center gap-1 text-muted-foreground"><EyeOff className="h-3 w-3" />Anonimo</span>}
                {p.multiple && <span className="inline-flex items-center gap-1 text-muted-foreground"><ListChecks className="h-3 w-3" />Multipla</span>}
                {p.has_voted && <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">Votato</span>}
              </div>
              <h3 className="mt-2 text-base font-semibold">{p.question}</h3>
              <p className="mt-3 text-xs text-muted-foreground">{p.participants} partecipanti{p.closes_at && ` · chiude ${fmtDateTime(p.closes_at)}`}</p>
            </Link>
          ))}
        </div>
      )}
      {open && <NewPollDialog open={open} onOpenChange={setOpen} onDone={load} />}
    </div>
  );
}
